import { isCookingStep, ok, replayBake, upcastEvent, } from '../domain/index.js';
import { exportBackup } from './backup.js';
/**
 * 2: `outcome`, `outcomeSource` and step `cooking` (contract 0.11.0). 3: `ingredients`, the
 * ingredient entry pins (contract 0.15.0).
 */
export const FEEDBACK_SCHEMA_VERSION = 3;
/** Read by whoever receives the export; data, not user interface text. */
export const FEEDBACK_ABOUT = 'Flourprint feedback export. `bakes` is a digest of each bake read from its event log: ' +
    'planned vs recorded step times (seconds, UTC instants), the accepted model estimate per step, ' +
    'observations and notes with the step they were taken on and the time elapsed in that step, ' +
    'and adjustments made during the bake. Each bake says whether it was real or simulated ' +
    '(`origin`; "unknown" for bakes recorded before this was asked); a doughReady observation ' +
    'says the stage the dough was ready for (`stage`: shape or bake); a fold observation says ' +
    "which set of folds was done (`set`, 1 … the count of the step technique's folds; a set may " +
    'be early, late, repeated or missing). Step techniques (method, mixing phases, folds, end ' +
    'check) are in the recipe revisions and bake snapshots of `backup`: recorded only, nothing ' +
    'is predicted from them. A finished bake (a Preparation) has an `outcome`: `result` cooked, ' +
    'dough (the raw dough is the result: frozen, refrigerated, given, sold) or cookedAndDough, ' +
    'with `outcomeSource` recipe (the default the recipe states, accepted at finish) or baker ' +
    '(chosen or corrected); "unknown" for bakes finished before outcomes were recorded, never ' +
    'read as cooked. Steps say whether they are a cooking stage (`cooking`); a completed cooking ' +
    'step never means the product is done. A session may be reopened and ended again; only its ' +
    'latest end counts. `flours` lists the flour ' +
    'registry entries ({id, version}) the bake used; absent for bakes recorded before the registry ' +
    '(unknown). Built-in entries are code and carry no composition; personal entries, with every ' +
    'version, are in `backup.flours`. Their properties (protein, ash, falling number, alveograph, ' +
    'malted) are recorded for later calibration and used by no calculation; unknown is never zero. ' +
    '`ingredients` lists the ingredient registry entries the snapshot formula pins ({ingredient: ' +
    'the formula ingredient id, entry: {id, version, customized?}}); absent when none. The formula ' +
    'holds a copy of the contents it consumed, possibly overridden by the baker (`overrides`), and ' +
    'a recorded `preparation`; personal entry versions are in `backup.ingredients`, built-in ones ' +
    'are code. A pin that does not resolve is "entry not found" and changes no number. ' +
    '`backup` is the authoritative record (recipes with ' +
    'every revision, raw bake events). Units: grams, degrees Celsius, seconds, baker percentages ' +
    'in points. `device` describes the exporting device only. Contracts: ' +
    'docs/contracts/history.md (feedback export), bake-session.md, process.md, fermentation.md, ' +
    'flours.md, ingredients.md.';
const seconds = (from, to) => from === undefined || to === undefined
    ? undefined
    : Math.round((Date.parse(to) - Date.parse(from)) / 1000);
/** Digest of one bake, read from its events only: never the current engine (history.md). */
export const bakeDigest = (bakeId, logged, recipeName) => {
    const upcast = [];
    for (const event of logged) {
        const current = upcastEvent(event);
        // A log this version cannot read stays in the backup, without a digest.
        if (!current.ok)
            return undefined;
        upcast.push(current.value);
    }
    const events = upcast;
    const state = replayBake(bakeId, events);
    const snapshot = state.snapshot;
    if (state.status === 'notStarted' || !snapshot)
        return undefined;
    const recordedAt = new Map(events.map((e) => [e.eventId, e.recordedAt]));
    const baseline = new Map(snapshot.process.steps.map((s) => [s.id, s]));
    const steps = (state.process ?? snapshot.process).steps.map((step) => {
        const record = state.steps[step.id];
        const planned = snapshot.initialProjection.steps[step.id];
        const estimate = snapshot.estimates[step.id];
        const baselineDurationS = baseline.get(step.id)?.plannedDurationS;
        const actualDurationS = seconds(record?.startedAt, record?.endedAt);
        return {
            id: step.id,
            operation: step.operation,
            cooking: isCookingStep(step),
            ...(step.label === undefined ? {} : { label: step.label }),
            ...(planned?.start === undefined ? {} : { plannedStartAt: planned.start }),
            ...(planned?.end === undefined ? {} : { plannedEndAt: planned.end }),
            ...(step.plannedDurationS === undefined ? {} : { plannedDurationS: step.plannedDurationS }),
            ...(baselineDurationS === undefined ? {} : { baselineDurationS }),
            ...(estimate === undefined || estimate.status !== 'ok' ? {} : { estimate }),
            ...(record?.startedAt === undefined ? {} : { startedAt: record.startedAt }),
            ...(record?.endedAt === undefined ? {} : { endedAt: record.endedAt }),
            ...(actualDurationS === undefined ? {} : { actualDurationS }),
        };
    });
    const observations = state.observations.map((o) => {
        const { type: _type, stepId, ...rest } = o.payload;
        const elapsedInStepS = stepId === undefined ? undefined : seconds(state.steps[stepId]?.startedAt, o.occurredAt);
        return {
            at: o.occurredAt,
            recordedAt: recordedAt.get(o.eventId) ?? o.occurredAt,
            ...(stepId === undefined ? {} : { stepId }),
            ...(elapsedInStepS === undefined ? {} : { elapsedInStepS }),
            ...rest,
        };
    });
    const adjustments = [];
    for (const event of events) {
        const { payload } = event;
        const reason = 'reason' in payload && payload.reason !== undefined ? payload.reason : undefined;
        const common = { at: event.occurredAt, ...(reason === undefined ? {} : { reason }) };
        if (payload.type === 'OverrideApplied' && 'effective' in payload) {
            adjustments.push({
                ...common,
                stepId: payload.target.stepId,
                field: payload.target.field,
                value: payload.effective,
            });
        }
        else if (payload.type === 'OverrideApplied' && 'edit' in payload) {
            adjustments.push({ ...common, field: 'formula', value: payload.edit });
        }
        else if (payload.type === 'StepInserted') {
            adjustments.push({ ...common, stepId: payload.step.id, field: 'insertStep', value: payload });
        }
    }
    const recipeId = snapshot.revisions.formula.id;
    const name = recipeName(recipeId);
    const pins = snapshot.formula.ingredients.flatMap((i) => i.entry === undefined ? [] : [{ ingredient: i.id, entry: i.entry }]);
    return {
        bakeId,
        status: state.status,
        ...(state.status !== 'finished'
            ? {}
            : state.outcome === undefined
                ? { outcome: 'unknown' }
                : {
                    outcome: state.outcome,
                    ...(state.outcomeSource === undefined ? {} : { outcomeSource: state.outcomeSource }),
                }),
        origin: snapshot.origin ?? 'unknown',
        recipe: {
            id: recipeId,
            ...(name === undefined ? {} : { name }),
            version: snapshot.revisions.formula.version,
        },
        ...(state.startedAt === undefined ? {} : { startedAt: state.startedAt }),
        ...(state.endedAt === undefined ? {} : { endedAt: state.endedAt }),
        manifest: snapshot.manifest,
        ...(snapshot.flours === undefined ? {} : { flours: snapshot.flours }),
        ...(pins.length === 0 ? {} : { ingredients: pins }),
        steps,
        observations,
        adjustments,
    };
};
/** The full backup with a digest per bake, for review by a person or an agent. */
export const exportFeedback = async (stores, exportedAt, device) => {
    const backup = await exportBackup(stores, exportedAt);
    if (!backup.ok)
        return backup;
    const names = new Map(backup.value.recipes.map((r) => [r.id, r.name]));
    return ok({
        kind: 'flourprint-feedback',
        schemaVersion: FEEDBACK_SCHEMA_VERSION,
        exportedAt,
        about: FEEDBACK_ABOUT,
        device,
        backup: backup.value,
        bakes: backup.value.bakes.flatMap(({ bakeId, events }) => {
            const digest = bakeDigest(bakeId, events, (id) => names.get(id));
            return digest ? [digest] : [];
        }),
    });
};
//# sourceMappingURL=feedback.js.map